// Filter rules, as data.
//
// A strategy is a JSON document a user can edit, share and store, so its
// filters cannot be predicates — they are { field, op, value } triples over a
// fixed vocabulary. Anything outside that vocabulary is rejected at validation
// rather than swallowed at run time.

import { sportLabel } from './teams.js';

/** The candidate fields a rule may name, and the type each one holds. */
export const RULE_FIELDS = {
  price: 'number',
  pFair: 'number',
  pModel: 'number',
  edge: 'number',
  ev: 'number',
  sport: 'string',
  bookmaker: 'string',
  hoursToKickoff: 'number',
  liquidity: 'number',
  isFavourite: 'boolean',
  // HOW FAR THE BOARD'S PRICE IS OVER THE BETFAIR HUB'S OWN RATED PRICE, as a
  // fraction of it: `price / ratedPrice - 1`. A SECOND OPINION, and a rule only
  // if somebody proves it — racing-value does not read it as shipped, and the
  // Hub itself publishes these models with "use at your own risk" on them. A
  // candidate on a race nobody rated has no value here at all, and `evalRule`
  // refuses a rule about a field the candidate has not got, which is the right
  // answer: a rating that does not exist is not a rating that agrees.
  ratingDisagreement: 'number',
};

export const RULE_OPS = ['eq', 'neq', 'in', 'between', 'gte', 'lte'];

/**
 * What each field is CALLED, and which ops it can take.
 *
 * These live here rather than in `lib/strategy-copy.js` because `ruleToWords`
 * is in this module and a rule printed two different ways is a rule nobody
 * trusts. The composer reads the same three tables the engine does.
 */
export const RULE_LABELS = {
  price: 'Price',
  pFair: 'Fair probability',
  pModel: 'Model probability',
  edge: 'Edge',
  ev: 'Expected value',
  sport: 'Sport',
  bookmaker: 'Bookmaker',
  hoursToKickoff: 'Hours to kick-off',
  liquidity: 'Liquidity',
  isFavourite: 'Is favourite',
  ratingDisagreement: 'Over the Hub rating',
};

/** Stored as a fraction, read and typed as a percentage. */
export const PERCENT_FIELDS = ['pFair', 'pModel', 'edge', 'ev', 'ratingDisagreement'];

/** The ops each type can actually use — the same rule `validateRule` enforces. */
export const OPS_FOR_TYPE = {
  number: ['gte', 'lte', 'between', 'eq', 'neq'],
  string: ['eq', 'neq', 'in'],
  boolean: ['eq'],
};

/** The ops a field will accept, in the order a menu should offer them. */
export const opsForField = (field) => OPS_FOR_TYPE[RULE_FIELDS[field]] ?? [];

const fail = (error) => ({ ok: false, error });
const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A rule is well-formed: known field, known op, and a value the op can use. */
export function validateRule(rule) {
  if (!isPlainObject(rule)) return fail('rule must be an object');
  for (const key of Object.keys(rule)) {
    if (!['field', 'op', 'value'].includes(key)) return fail(`unknown rule key: ${key}`);
  }
  const type = RULE_FIELDS[rule.field];
  if (!type) return fail(`unknown rule field: ${rule.field}`);
  if (!RULE_OPS.includes(rule.op)) return fail(`unknown rule op: ${rule.op}`);

  if (rule.op === 'in') {
    if (!Array.isArray(rule.value) || rule.value.length === 0) return fail(`${rule.field} in: value must be a non-empty array`);
    if (rule.value.some((v) => typeof v !== type)) return fail(`${rule.field} in: every value must be a ${type}`);
    return { ok: true };
  }
  if (rule.op === 'between') {
    if (type !== 'number') return fail(`${rule.field} between: only number fields have a range`);
    if (!Array.isArray(rule.value) || rule.value.length !== 2) return fail(`${rule.field} between: value must be [lo, hi]`);
    const [lo, hi] = rule.value;
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return fail(`${rule.field} between: lo and hi must be numbers`);
    if (lo > hi) return fail(`${rule.field} between: lo must not exceed hi`);
    return { ok: true };
  }
  if (rule.op === 'gte' || rule.op === 'lte') {
    if (type !== 'number') return fail(`${rule.field} ${rule.op}: only number fields are ordered`);
    if (!Number.isFinite(rule.value)) return fail(`${rule.field} ${rule.op}: value must be a number`);
    return { ok: true };
  }
  // eq / neq
  if (typeof rule.value !== type) return fail(`${rule.field} ${rule.op}: value must be a ${type}`);
  if (type === 'number' && !Number.isFinite(rule.value)) return fail(`${rule.field} ${rule.op}: value must be finite`);
  return { ok: true };
}

/** Does one candidate satisfy one rule? A field the candidate hasn't got fails. */
export function evalRule(rule, candidate) {
  const actual = candidate?.[rule.field];
  if (actual === undefined || actual === null) return false;
  switch (rule.op) {
    case 'eq': return actual === rule.value;
    case 'neq': return actual !== rule.value;
    case 'in': return rule.value.includes(actual);
    case 'between': return actual >= rule.value[0] && actual <= rule.value[1];
    case 'gte': return actual >= rule.value;
    case 'lte': return actual <= rule.value;
    default: return false;
  }
}

/** Every rule must pass. No rules is no filter. */
export function passesRules(rules, candidate) {
  return (rules ?? []).every((rule) => evalRule(rule, candidate));
}

// --- saying it out loud --------------------------------------------------------

/** A number with the noise off: 3.0000000000000004 prints as 3. */
const tidy = (n) => String(+Number(n).toFixed(4));

/** One value, in the units the READER thinks in. */
function valueWords(field, value) {
  if (RULE_FIELDS[field] === 'string') return field === 'sport' ? sportLabel(value) : String(value);
  if (PERCENT_FIELDS.includes(field)) return `${tidy(Number(value) * 100)}%`;
  // Prices are always two places: 1.4 and 1.40 are the same price and a band
  // printed as "1.4 and 2.2" reads like a typo next to a ticket.
  if (field === 'price') return Number(value).toFixed(2);
  return tidy(value);
}

/**
 * One rule in plain words: "Price between 1.40 and 2.20", "Sport in AFL, NRL",
 * "Is favourite".
 *
 * A rule that would not validate returns the empty string rather than a
 * half-sentence — the composer is showing a preview of a rule still being
 * typed, and "Price between 2.20 and undefined" is worse than nothing.
 */
export function ruleToWords(rule) {
  if (!validateRule(rule).ok) return '';
  const label = RULE_LABELS[rule.field] ?? rule.field;

  if (RULE_FIELDS[rule.field] === 'boolean') {
    // eq true and neq false both mean yes. The label already reads "Is …", so
    // the negative is the same sentence with the verb swapped.
    const yes = (rule.op === 'eq') === (rule.value === true);
    return yes ? label : label.replace(/^Is /, 'Not ');
  }

  const v = (x) => valueWords(rule.field, x);
  switch (rule.op) {
    case 'eq': return `${label} is ${v(rule.value)}`;
    case 'neq': return `${label} is not ${v(rule.value)}`;
    case 'in': return `${label} in ${rule.value.map(v).join(', ')}`;
    case 'between': return `${label} between ${v(rule.value[0])} and ${v(rule.value[1])}`;
    case 'gte': return `${label} at least ${v(rule.value)}`;
    case 'lte': return `${label} at most ${v(rule.value)}`;
    default: return '';
  }
}

/** Every rule, one line, in the order they are applied. Unfinished rules drop out. */
export function rulesToWords(rules) {
  return (rules ?? []).map(ruleToWords).filter(Boolean).join(' · ');
}
